import { Link } from "react-router-dom";
import { ArrowUp, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY } from "../data/company";
import { PRODUCTS } from "../data/products";
import Logo from "./Logo";
import { RailDivider } from "./Backdrop";

export default function Footer() {
  const productLinks = PRODUCTS.filter((p) => !p.comingSoon).slice(0, 5);

  const toTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative overflow-hidden bg-[#F3F7FB]" data-testid="footer">
      <div className="absolute inset-0 blueprint-grid opacity-60" aria-hidden="true" />
      <div className="wrap relative pt-16 md:pt-20">
        <RailDivider className="mb-14" />
        <div className="grid gap-12 pb-14 md:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_1fr_1.3fr]">
          <div>
            <Logo />
            <p className="mt-6 font-display text-2xl font-bold uppercase tracking-wide">
              <span className="accent-text">{COMPANY.tagline}</span>
            </p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
              {COMPANY.secondaryTagline}
            </p>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-slate-500">
              Railway electrical, electromechanical and engineering components — manufactured and supplied with a
              focus on quality, reliability and precision.
            </p>
            <div className="mt-6 flex gap-3">
              {COMPANY.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  aria-label={`${s.name} (placeholder link)`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-900/15 text-slate-500 transition hover:border-brand-emeraldLight hover:text-brand-emeraldDark"
                >
                  <span className="font-mono text-[10px] uppercase">{s.name.slice(0, 2)}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-emeraldDark">Quick Links</h3>
            <ul className="mt-5 space-y-2.5 text-sm">
              {COMPANY.nav.slice(0, 6).map((n) => (
                <li key={n.path}>
                  <Link to={n.path} className="text-slate-500 transition hover:text-ink">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-emeraldDark">Products</h3>
            <ul className="mt-5 space-y-2.5 text-sm">
              {productLinks.map((p) => (
                <li key={p.id}>
                  <Link to={`/products/${p.id}`} className="text-slate-500 transition hover:text-ink">
                    {p.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/products" className="font-medium text-brand-emeraldDark transition hover:text-ink">
                  View All Products
                </Link>
              </li>
            </ul>
          </div>

          <div className="space-y-5 text-sm">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-emeraldDark">Contact</h3>
            <p className="flex gap-3 text-slate-500">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-blueDark" />
              <span>
                <strong className="block text-slate-600">{COMPANY.office.label}</strong>
                {COMPANY.office.lines.join(" ")}
              </span>
            </p>
            <p className="flex gap-3 text-slate-500">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-blueDark" />
              <span>
                <strong className="block text-slate-600">{COMPANY.manufacturing.label}</strong>
                {COMPANY.manufacturing.lines.join(" ")}
              </span>
            </p>
            <div className="space-y-1.5">
              {COMPANY.emails.map((e) => (
                <a key={e} href={`mailto:${e}`} className="flex items-center gap-3 text-slate-500 transition hover:text-brand-emeraldDark">
                  <Mail className="h-4 w-4 shrink-0 text-brand-blueDark" /> {e}
                </a>
              ))}
            </div>
            <div className="space-y-1.5">
              {COMPANY.phones.map((p) => (
                <a key={p.link} href={`tel:${p.link}`} className="flex items-center gap-3 text-slate-500 transition hover:text-brand-emeraldDark">
                  <Phone className="h-4 w-4 shrink-0 text-brand-blueDark" /> {p.display}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-navy-900/[0.08] py-7 sm:flex-row">
          <p className="text-xs text-slate-500">{COMPANY.copyright}</p>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-700">
              Railway Engineering · India
            </span>
            <button
              type="button"
              onClick={toTop}
              data-testid="footer-back-to-top"
              aria-label="Back to top"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-900/15 text-slate-500 transition hover:border-brand-emeraldLight hover:text-brand-emeraldDark"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <img
        src={COMPANY.logo.src}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -right-10 h-80 w-auto opacity-[0.05]"
      />
    </footer>
  );
}
